import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import ClockIcon from '@atlaskit/icon/core/clock';

import type {
	ForYouConfig,
	NavItemConfig,
	ObjectListFlyoutItemConfig,
	Recents,
	RecentsHidden,
} from '../../../common/types';
import type { Action } from '../../../ui/object-list-flyout/types';

import { RECENT_TEST_ID } from './constants';
import { messages } from './messages';

const elemBefore = <ClockIcon label="" />;

export function useRecentNavItem(forYou: ForYouConfig): [string, NavItemConfig][] {
	const { formatMessage } = useIntl();
	// Some type magic so we can get the dependencies right for the memo and keep TypeScript happy.
	// Only use this constant when `forYou.hideRecents` is false.
	const recentShown = forYou as Exclude<Recents, RecentsHidden>;

	return useMemo(() => {
		if (forYou.hideRecents) {
			return [];
		} else {
			const actions: Action[] | undefined = recentShown.viewAllRecentsUrl
				? [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: formatMessage(messages.viewAllRecentItemsLabel),
							href: recentShown.viewAllRecentsUrl,
							onClick: recentShown.viewAllRecentsOnClick,
						},
					]
				: recentShown.viewAllRecentsOnClick && [
						{
							elemBefore: <AlignTextLeftIcon label={''} />,
							label: formatMessage(messages.viewAllRecentItemsLabel),
							onClick: recentShown.viewAllRecentsOnClick,
						},
					];

			const filterConfig = recentShown.recentsFilters
				? { filters: recentShown.recentsFilters }
				: undefined;

			const value: ObjectListFlyoutItemConfig = {
				type: 'object-list-flyout',
				elemBefore,
				label: formatMessage(messages.label),
				actions,
				filterConfig,
				isSearchable: recentShown.recentsIsSearchable,
				searchPlaceholder: formatMessage(messages.searchPlaceholder),
				testId: RECENT_TEST_ID,
				useItems: recentShown.useRecentItems,
			};

			return [[recentShown.recentsMenuItemId ?? 'global.sidebar.recent', value]];
		}
	}, [
		forYou.hideRecents,
		formatMessage,
		recentShown.recentsFilters,
		recentShown.recentsIsSearchable,
		recentShown.recentsMenuItemId,
		recentShown.useRecentItems,
		recentShown.viewAllRecentsOnClick,
		recentShown.viewAllRecentsUrl,
	]);
}
